"use client";

import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { cn } from "@/app/lib/utils";
import { getSocialLinks, type SocialLink } from "@/app/lib/data";
import { useTheme } from "next-themes";

// Define types for brand colors
interface BrandColorConfig {
  color: string;
  hover: string;
  darkColor?: string;
  darkHover?: string;
}

interface SocialBrandColors {
  [key: string]: BrandColorConfig;
}

// Define brand colors for social platforms
const SOCIAL_BRAND_COLORS: SocialBrandColors = {
  linkedin: {
    color: "#0A66C2",
    hover: "#0077B5",
  },
  twitter: {
    color: "#000000", // X (Twitter) black for light mode
    hover: "#141414", // Slightly lighter black for light mode hover
    darkColor: "#FFFFFF", // White for dark mode
    darkHover: "#E6E6E6", // Slightly dimmed white for dark mode hover
  },
  x: {
    color: "#000000", // X (Twitter) black for light mode
    hover: "#141414", // Slightly lighter black for light mode hover
    darkColor: "#FFFFFF", // White for dark mode
    darkHover: "#E6E6E6", // Slightly dimmed white for dark mode hover
  },
  github: {
    color: "#333333",
    hover: "#24292e",
    darkColor: "#ffffff",
    darkHover: "#e6e6e6",
  },
  email: {
    color: "#D44638",
    hover: "#B23121",
  },
};

// Extended SocialLink type with additional properties (fields already exist in SocialLink)
type ExtendedSocialLink = SocialLink;

export interface SocialIconsProps {
  /** Adjust tooltip position: "top", "bottom", "left", "right" */
  tooltipPosition?: "top" | "bottom" | "left" | "right";
  /** Center icons or use flex-start, space-between, etc. */
  justifyContent?: string;
  /** Size of the icons: "sm" (8), "md" (10), "lg" (12) */
  size?: "sm" | "md" | "lg";
  /** Space between icons */
  spacing?: "tight" | "normal" | "wide";
  /** Optional className for container */
  className?: string;
}

/**
 * Shared SocialIcons component
 * Used in header, sidebar, footer, etc.
 * Fetches social links from Sanity CMS
 */
export function SocialIcons({
  tooltipPosition = "bottom",
  justifyContent = "center",
  size = "md",
  spacing = "normal",
  className,
}: SocialIconsProps) {
  const [socialLinks, setSocialLinks] = useState<ExtendedSocialLink[]>([]);
  const [socialIconStates, setSocialIconStates] = useState<
    Array<{ currentIcon: string; isHovered: boolean }>
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const { theme } = useTheme();

  // Size classes
  const sizeClasses = {
    sm: "h-8 w-8",
    md: "h-10 w-10",
    lg: "h-12 w-12",
  };

  // Spacing classes
  const spacingClasses = {
    tight: "space-x-2",
    normal: "space-x-3",
    wide: "space-x-4",
  };

  // Tooltip positioning
  const tooltipClasses = {
    top: "bottom-full mb-2 left-1/2 transform -translate-x-1/2",
    bottom: "top-full mt-2 left-1/2 transform -translate-x-1/2",
    left: "right-full mr-2 top-1/2 transform -translate-y-1/2",
    right: "left-full ml-2 top-1/2 transform -translate-y-1/2",
  };

  // Tooltip arrow positioning
  const arrowClasses = {
    top: "absolute -bottom-1 left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800",
    bottom:
      "absolute -top-1 left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-transparent border-b-slate-800",
    left: "absolute -right-1 top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-l-4 border-transparent border-l-slate-800",
    right:
      "absolute -left-1 top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-r-4 border-transparent border-r-slate-800",
  };

  const getSocialBrandColors = (iconName: string, isHovered: boolean): string | null => {
    const getPlatform = (icon: string): keyof typeof SOCIAL_BRAND_COLORS | null => {
      if (icon.includes('linkedin')) return 'linkedin';
      if (icon.includes('twitter')) return 'twitter';
      if (icon.includes('x-')) return 'x';
      if (icon.includes('github')) return 'github';
      if (icon.includes('email')) return 'email';
      return null;
    };

    const platform = getPlatform(iconName);
    if (!platform) return null;

    const brandColors = SOCIAL_BRAND_COLORS[platform];
    const isDarkMode = theme === 'dark';

    // Handle dark mode colors for Github, Twitter, and X
    if ((platform === 'github' || platform === 'twitter' || platform === 'x') && isDarkMode) {
      return isHovered ? brandColors.darkHover! : brandColors.darkColor!;
    }

    return isHovered ? brandColors.hover : brandColors.color;
  };

  // Fetch social links from Sanity
  useEffect(() => {
    async function fetchSocialLinks() {
      setIsLoading(true);
      try {
        const links = await getSocialLinks();
        setSocialLinks(links);
        // Initialize icon states
        setSocialIconStates(
          links.map((link) => ({
            currentIcon: link.icon,
            isHovered: false,
          }))
        );
      } catch (error) {
        console.error("Error fetching social links:", error);
        setSocialLinks([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchSocialLinks();
  }, []);

  if (isLoading) {
    // Optional loading state - could be replaced with skeleton icons
    return (
      <div
        className={cn(
          "flex items-center",
          spacingClasses[spacing],
          `justify-${justifyContent}`,
          className
        )}
      >
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className={cn(
              "rounded-full bg-slate-200 animate-pulse",
              sizeClasses[size]
            )}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center",
        spacingClasses[spacing],
        `justify-${justifyContent}`,
        className
      )}
    >
      {socialLinks.map((link, index) => {
        // Function to update icon state on hover
        const updateIconState = (isHovered: boolean) => {
          setSocialIconStates((prev) => {
            const newState = [...prev];
            if (newState[index]) {
              newState[index] = {
                currentIcon:
                  isHovered && link.hoverIcon ? link.hoverIcon : link.icon,
                isHovered,
              };
            }
            return newState;
          });
        };

        const iconState = socialIconStates[index];
        if (!iconState) return null;

        const { currentIcon, isHovered } = iconState;

        const brandColor = getSocialBrandColors(link.icon, isHovered);

        // Check if the icon is LinkedIn or Twitter/X
        const isLinkedInOrTwitter =
          link.icon.includes("linkedin") ||
          link.icon.includes("twitter") ||
          link.icon.includes("x");

        return (
          <div key={link._id} className="relative group">
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "flex items-center justify-center rounded-full transition-all duration-300",
                "border border-slate-300 dark:border-slate-700 hover:border-[#2b505c] dark:hover:border-[#2b505c]",
                "bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800/50",
                "focus:outline-none focus:ring-2 focus:ring-[#2b505c]/50 focus:ring-offset-2",
                "hover:scale-105 active:scale-95",
                sizeClasses[size]
              )}
              style={{
                color: brandColor || link.iconColor || 'currentColor',
              }}
              aria-label={link.ariaLabel || link.title}
              onMouseEnter={() => updateIconState(true)}
              onMouseLeave={() => updateIconState(false)}
              onFocus={() => updateIconState(true)}
              onBlur={() => updateIconState(false)}
            >
              <Icon
                icon={currentIcon}
                width={size === "sm" ? "16" : size === "md" ? "20" : "24"}
                height={size === "sm" ? "16" : size === "md" ? "20" : "24"}
                className={cn(
                  "transition-transform duration-300",
                  isHovered ? "scale-110" : "scale-100"
                )}
                style={{
                  color: brandColor || (isHovered && link.hoverIconColor
                    ? link.hoverIconColor
                    : link.iconColor || 'currentColor'),
                }}
              />
            </a>

            {/* Tooltip */}
            <div
              className={cn(
                "absolute opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none",
                tooltipClasses[tooltipPosition]
              )}
            >
              <div className="bg-slate-800 text-white text-xs font-medium px-2 py-1 rounded shadow-lg whitespace-nowrap">
                {link.title}
              </div>
              <div className={arrowClasses[tooltipPosition]} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
